import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Clock, AlertTriangle, XCircle, Zap, X } from "lucide-react";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TrialBanner — Shows trial status at top of the app for new Pro trial flow.
 * 
 * NEW Trial Flow:
 * - All new users get 14-day Pro trial automatically (no credit card)
 * - Banner shows days remaining with color-coded urgency
 * - "Ver planes" button opens upgrade modal
 * 
 * Urgency levels:
 *   - Days 1-10:  "info" (teal) — dismissible
 *   - Days 11-12: "warning" (amber)
 *   - Day 13:     "urgent" (orange)
 *   - Day 14:     "critical" (red) — NOT dismissible
 *   - Day 15+:    "expired" (persistent red) — trial ended
 */

const STYLES = {
  info: {
    bg: "bg-teal-500/10 border-teal-500/30",
    text: "text-teal-300",
    icon: Clock,
    iconColor: "text-teal-400",
    buttonClass: "bg-teal-600 hover:bg-teal-500 text-white",
    dismissible: true,
  },
  warning: {
    bg: "bg-amber-500/15 border-amber-500/40",
    text: "text-amber-200",
    icon: AlertTriangle,
    iconColor: "text-amber-400",
    buttonClass: "bg-amber-600 hover:bg-amber-500 text-white",
    dismissible: false,
  },
  urgent: {
    bg: "bg-orange-500/15 border-orange-500/40",
    text: "text-orange-200",
    icon: AlertTriangle,
    iconColor: "text-orange-400",
    buttonClass: "bg-orange-600 hover:bg-orange-500 text-white",
    dismissible: false,
  },
  critical: {
    bg: "bg-red-500/15 border-red-500/40",
    text: "text-red-200",
    icon: XCircle,
    iconColor: "text-red-400",
    buttonClass: "bg-red-600 hover:bg-red-500 text-white",
    dismissible: false,
  },
  expired: {
    bg: "bg-red-500/20 border-red-500/50",
    text: "text-red-100",
    icon: XCircle,
    iconColor: "text-red-300",
    buttonClass: "bg-red-600 hover:bg-red-500 text-white",
    dismissible: false,
  },
};

export default function TrialBanner({ onOpenPlans }) {
  const [trialStatus, setTrialStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  const fetchTrialStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/billing/trial-status`, { withCredentials: true });
      if (res.data?.is_trial || res.data?.status === "expired") {
        setTrialStatus(res.data);
        setDismissed(false);
      } else {
        setTrialStatus(null);
      }
    } catch {
      // Silently fail — banner just won't show
      setTrialStatus(null);
    }
  }, []);

  useEffect(() => {
    fetchTrialStatus();
    // Re-check every 5 minutes
    const interval = setInterval(fetchTrialStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchTrialStatus]);

  const handleViewPlans = () => {
    if (onOpenPlans) {
      onOpenPlans();
    } else {
      // Fallback: navigate to organization settings
      navigate("/organization-settings");
    }
  };

  // Don't render if no trial or if dismissed (only dismissible for "info" level)
  if (!trialStatus) return null;
  if (dismissed && trialStatus.urgency === "info") return null;

  const style = STYLES[trialStatus.urgency] || STYLES.info;
  const IconComponent = style.icon;
  const canDismiss = style.dismissible && trialStatus.urgency === "info";

  return (
    <div className={`w-full border-b ${style.bg} px-4 py-2.5 flex items-center justify-between gap-4`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <IconComponent className={`w-5 h-5 flex-shrink-0 ${style.iconColor}`} />
        <span className={`text-sm font-medium ${style.text} truncate`}>
          {trialStatus.message}
        </span>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          size="sm"
          onClick={handleViewPlans}
          className={`h-8 px-4 text-xs font-semibold ${style.buttonClass}`}
        >
          <span className="flex items-center gap-1.5">
            <Zap size={14} />
            Ver planes
          </span>
        </Button>

        {canDismiss && (
          <button
            onClick={() => setDismissed(true)}
            className="p-1 rounded hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className={`w-4 h-4 ${style.text}`} />
          </button>
        )}
      </div>
    </div>
  );
}
